import { ElementRef, Injectable, Injector } from '@angular/core';
import { ConnectionPositionPair, Overlay, OverlayConfig, PositionStrategy } from '@angular/cdk/overlay';
import { ComponentPortal, PortalInjector } from '@angular/cdk/portal';
import { PopoverTableContent, PopoverTableRef } from './popover-table-ref';
import { PopoverTableComponent } from './popover-table.component';

export interface PopoverTableParams<T> {
  width?: string | number;
  height?: string | number;
  origin: ElementRef | HTMLElement;
  position?: ConnectionPositionPair[];
  title: string;
  data?: T;
  offsetY?: number;
  offsetX?: number;
}

@Injectable({
  providedIn: 'root'
})
export class PopoverTableService {
  constructor(private overlay: Overlay, private injector: Injector) { }

  popoverTable: PopoverTableRef<any>;

  open<T>({ origin, title, data, width, height, position, offsetX, offsetY }: PopoverTableParams<T>): PopoverTableRef<T> {
    const overlayRef = this.overlay.create(this.getOverlayConfig({ origin, width, height, position, offsetX, offsetY }));
    const popoverTableRef = new PopoverTableRef<T>(overlayRef, title, data);

    const injector = this.createInjector(popoverTableRef, this.injector);
    overlayRef.attach(new ComponentPortal(PopoverTableComponent, null, injector));

    this.popoverTable = popoverTableRef;

    return popoverTableRef;
  }

  createInjector(popoverTableRef: PopoverTableRef, injector: Injector) {
    const tokens = new WeakMap([[PopoverTableRef, popoverTableRef]]);
    return new PortalInjector(injector, tokens);
  }

  close() {
    this.popoverTable.close();
  }

  private getOverlayConfig({ origin, width, height, position, offsetX, offsetY }): OverlayConfig {
    return new OverlayConfig({
      hasBackdrop: true,
      width,
      height,
      backdropClass: 'popover-table-backdrop',
      positionStrategy: this.getOverlayPosition({ origin, position, offsetX, offsetY }),
      scrollStrategy: this.overlay.scrollStrategies.reposition()
    });
  }

  private getOverlayPosition({ origin, position, offsetX, offsetY }): PositionStrategy {
    const positionStrategy = this.overlay.position()
      .flexibleConnectedTo(origin)
      .withPositions(position || this.getPositions())
      .withFlexibleDimensions(true)
      .withDefaultOffsetY(offsetY || 0)
      .withDefaultOffsetX(offsetX || 0)
      .withTransformOriginOn('.popover-table')
      .withPush(true);

    return positionStrategy;
  }

  private getPositions(): ConnectionPositionPair[] {
    return [
      {
        originX: 'center',
        originY: 'top',
        overlayX: 'center',
        overlayY: 'bottom'
      },
      {
        originX: 'center',
        originY: 'bottom',
        overlayX: 'center',
        overlayY: 'top',
      },
    ];
  }

}
